<template>
  <!-- BEGIN: Top Bar -->
  <div id="topbar" :class="[
    'sm:grid md:flex items-center relative border-b justify-between ',
    {
      'h-[67px] border-slate-200 z-[50]': layout === 'rubick',
    },
    {
      'z-[50] md:z-[52] mt-[70px] md:mt-0 h-full h-[70px] py-4 md:py-0 md:h-[65px] border-white/[0.08] md:mt-0 md:-mx-0 px-4 md:border-b-0 md:inset-x-0 md:top-0 md:px-10 md:pt-10 md:bg-gradient-to-b md:from-slate-100 md:to-transparent dark:md:from-darkmode-700':
        layout === 'enigma',
      'before:content-[\'\'] before:absolute before:h-[65px] before:inset-0 before:top-0 before:mx-7 before:bg-primary/30 before:mt-3 before:rounded-xl before:hidden before:md:block before:dark:bg-darkmode-600/30':
        layout === 'enigma',
      'after:content-[\'\'] after:absolute after:inset-0 after:h-[65px] after:mx-3 after:bg-primary after:mt-5 after:rounded-xl after:shadow-md after:hidden after:md:block after:dark:bg-darkmode-600':
        layout === 'enigma',
    },
    {
      'z-[50] h-[70px] border-white/[0.08] mt-12 md:-mt-5 -mx-3 sm:-mx-8 px-3 sm:px-8 md:pt-0':
        layout === 'icewall',
    },
    {
      'z-[50] h-[67px] border-slate-200': layout === 'tinker',
    },
  ]">
    <!-- BEGIN: Breadcrumb -->
    <Breadcrumb :light="layout === 'enigma' || layout === 'icewall'" :class="[
      {
        '-intro-x sm:flex': layout === 'rubick',
      },
      {
        'h-[45px] border-white/[0.08] dark:border-white/[0.08]  -intro-x':
          layout === 'enigma',
      },
      {
        'h-full border-white/[0.08] -intro-x': layout === 'icewall',
      },
      {
        '-intro-x sm:flex': layout === 'tinker',
      },
    ]">
      <a class="cursor-pointer" @click="goToDashboard()">{{ $t(Labels.managementId) }}</a>
      <Breadcrumb.Link @click="goToDashboard()"> {{ label }} </Breadcrumb.Link>
      <Breadcrumb.Link v-if="breadcrumb != 'Management'" :active="true"> {{ breadcrumb }} </Breadcrumb.Link>
    </Breadcrumb>
    <!-- END: Breadcrumb -->
    <div class="flex items-center gap-4 sm:pr-2">
      <!-- BEGIN: Search -->
      <div class="relative intro-x">
        <div class="relative hidden sm:block" id="profileSection">
          <FormInput type="text"
            class="border-transparent w-56 shadow-none rounded-full pr-8 transition-[width] duration-300 ease-in-out focus:border-transparent focus:w-72 dark:bg-darkmode-400/70"
            :class="[
              {
                'bg-slate-300/50': layout === 'rubick',
              },
            ]" :placeholder="$t(Labels.searchId)" @focus="showSearchDropdown" @blur="hideSearchDropdown" />
          <Lucide icon="Search"
            class="absolute inset-y-0 right-0 w-5 h-5 my-auto mr-3 text-slate-600 dark:text-slate-500" />
        </div>
        <a class="relative text-slate-600 sm:hidden" href="">
          <Lucide icon="Search" class="w-5 h-5 dark:text-slate-500" />
        </a>
        <TransitionRoot as="template" :show="searchDropdown" enter="transition-all ease-linear duration-150"
          enterFrom="mt-5 invisible opacity-0 translate-y-1" enterTo="mt-[3px] visible opacity-100 translate-y-0"
          entered="mt-[3px]" leave="transition-all ease-linear duration-150"
          leaveFrom="mt-[3px] visible opacity-100 translate-y-0" leaveTo="mt-5 invisible opacity-0 translate-y-1">
          <div class="absolute right-0 z-10 mt-[3px]">
            <div class="w-[450px] p-5 box">
              <div class="mb-2 font-medium">{{ $t(Labels.pagesId) }}</div>
              <div class="mb-5">
                <a href="" class="flex items-center">
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full bg-success/20 dark:bg-success/10 text-success">
                    <Lucide icon="Inbox" class="w-4 h-4" />
                  </div>
                  <div class="ml-3">{{ $t(Labels.mailSettingsId) }}</div>
                </a>
                <a href="" class="flex items-center mt-2">
                  <div class="flex items-center justify-center w-8 h-8 rounded-full bg-pending/10 text-pending">
                    <Lucide icon="Users" class="w-4 h-4" />
                  </div>
                  <div class="ml-3">{{ $t(Labels.usersPermissionsId) }}</div>
                </a>
                <a href="" class="flex items-center mt-2">
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 text-primary/80">
                    <Lucide icon="CreditCard" class="w-4 h-4" />
                  </div>
                  <div class="ml-3">{{ $t(Labels.transactionsReportId) }}</div>
                </a>
              </div>
              <div class="mb-2 font-medium">{{ $t(Labels.usersId) }}</div>
              <div class="mb-5">
                <a v-for="(faker, fakerKey) in _.take(fakerData, 4)" :key="fakerKey" href=""
                  class="flex items-center mt-2">
                  <div class="w-8 h-8 image-fit">
                    <img alt="Midone Tailwind HTML Admin Template" class="rounded-full" :src="faker.photos[0]" />
                  </div>
                  <div class="ml-3">{{ faker.users[0].name }}</div>
                  <div class="w-48 ml-auto text-xs text-right truncate text-slate-500">
                    {{ faker.users[0].email }}
                  </div>
                </a>
              </div>
              <div class="mb-2 font-medium">{{ $t(Labels.productsId) }}</div>
              <a v-for="(faker, fakerKey) in _.take(fakerData, 4)" :key="fakerKey" href=""
                class="flex items-center mt-2">
                <div class="w-8 h-8 image-fit">
                  <img alt="Midone Tailwind HTML Admin Template" class="rounded-full" :src="faker.images[0]" />
                </div>
                <div class="ml-3">{{ faker.products[0].name }}</div>
                <div class="w-48 ml-auto text-xs text-right truncate text-slate-500">
                  {{ faker.products[0].category }}
                </div>
              </a>
            </div>
          </div>
        </TransitionRoot>
      </div>
      <!-- END: Search  -->
      <!--BEGIN: Hints-->
      <font-awesome-icon :icon="['fas', 'exclamation']" @click="startHints()" data-intro='Hello step one!'
        class="cursor-pointer w-5 h-5 dark:text-slate-500" />
      <!--END: Hints-->
      <!-- BEGIN: Notifications -->
      <Popover class="intro-x">
        <Popover.Button
          class="relative outline-none block before:content-[''] before:w-[8px] before:h-[8px] before:rounded-full before:absolute before:top-[-2px] before:right-0 before:bg-danger"
          :class="{
            'text-slate-600': layout === 'rubick',
            'text-white/70':
              layout === 'enigma' || layout === 'icewall',
          }">
          <Lucide icon="Bell" class="w-5 h-5 dark:text-slate-500" id="menuButton" />
        </Popover.Button>
        <Popover.Panel :as="'div'" :placement="calculatePlacement()" class="w-[280px] sm:w-[350px] p-5 mt-2">
          <div class="mb-5 font-medium">{{ $t(Labels.notificationsId) }}</div>
          <div @click="goToInbox" v-for="(faker, fakerKey) in _.take(fakerData, 5)" :key="fakerKey" :class="[
            'cursor-pointer relative flex items-center',
            { 'mt-5': fakerKey },
          ]">
            <div class="relative flex-none w-12 h-12 mr-1 image-fit">
              <img alt="Midone Tailwind HTML Admin Template" class="rounded-full" :src="faker.photos[0]" />
              <div
                class="absolute bottom-0 right-0 w-3 h-3 border-2 border-white rounded-full bg-success dark:border-darkmode-600">
              </div>
            </div>
            <div class="ml-2 overflow-hidden">
              <div class="flex items-center">
                <a class="mr-5 font-medium truncate">
                  {{ faker.users[0].name }}
                </a>
                <div class="ml-auto text-xs text-slate-400 whitespace-nowrap">
                  {{ faker.times[0] }}
                </div>
              </div>
              <div class="w-full truncate text-slate-500 mt-0.5">
                {{ faker.news[0].shortContent }}
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Popover>
      <!-- END: Notifications  -->
      <!--BEGIN: Swicher -->
      <ThemeSettings id="settings" :layout="layout" @selectTheme="selectTheme"></ThemeSettings>
      <!-- END: Switcher -->
      <!-- BEGIN: Account Menu -->
      <!-- <Menu>
        <Menu.Button
          class="block w-8 h-8 overflow-hidden rounded-full shadow-lg image-fit zoom-in intro-x"
        >
          <img
            alt="Midone Tailwind HTML Admin Template"
            :src="fakerData[9].photos[0]"
          />
        </Menu.Button>
        <Menu.Items class="w-56 mt-px text-white bg-primary">
          <Menu.Header class="font-normal">
            <div class="font-medium">{{ fakerData[0].users[0].name }}</div>
            <div class="text-xs text-white/70 mt-0.5 dark:text-slate-500">
              {{ fakerData[0].jobs[0] }}
            </div>
          </Menu.Header>
          <Menu.Divider class="bg-white/[0.08]" />
          <Menu.Item class="hover:bg-white/5">
            <Lucide icon="User" class="w-4 h-4 mr-2" /> Profile
          </Menu.Item>
          <Menu.Item class="hover:bg-white/5">
            <Lucide icon="Edit" class="w-4 h-4 mr-2" /> Add Account
          </Menu.Item>
          <Menu.Item class="hover:bg-white/5">
            <Lucide icon="Lock" class="w-4 h-4 mr-2" /> Reset Password
          </Menu.Item>
          <Menu.Item class="hover:bg-white/5">
            <Lucide icon="HelpCircle" class="w-4 h-4 mr-2" /> Help
          </Menu.Item>
          <Menu.Divider class="bg-white/[0.08]" />
          <Menu.Item class="hover:bg-white/5">
            <Lucide icon="ToggleRight" class="w-4 h-4 mr-2" /> Logout
          </Menu.Item>
        </Menu.Items>
      </Menu> -->
      <!-- END: Account Menu -->
    </div>
  </div>
  <!-- END: Top Bar -->
</template>

<script setup lang="ts">
import { ref, watch, defineProps, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import LayoutSwitcher from "../../base-components/LayoutSwitcher";
import ThemeSettings from "@/components/ThemeSettings/ThemeSettings.vue";
import Lucide from "../../base-components/Lucide";
import Breadcrumb from "../../base-components/Breadcrumb";
import { FormInput } from "../../base-components/Form";
import { Menu, Popover } from "../../base-components/Headless";
import { getToolTips } from "@/services/backend/backend.service";
import _ from "lodash";
import { TransitionRoot } from "@headlessui/vue";
import fakerData from "../../utils/faker";
import { loadMessages } from '../../services/translation/translation.service';
import { Labels } from '../constants.enum';
import introJs from "intro.js";
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps({
  layout: String,
});
const store = useStore();
const rightDiv = ref("");
const searchDropdown = ref(false);
const showSearchDropdown = () => {
  searchDropdown.value = true;
};
const router = useRouter();
const route = useRoute();
const breadcrumb = ref("");
const key = ref(0);
const label = ref(localStorage.getItem("breadcrumb"));
const toolTipsData = ref([]);
const toolTipsDetail = ref([]);
const translatedToolTips = ref({});

onMounted(async () => {

  const res = await getToolTips();
  toolTipsData.value = res.data?.response?.records?.master || [];
  toolTipsDetail.value = res.data?.response?.records?.detail || [];

  // Extract keys to translate from master
  const keysToTranslateMaster = toolTipsData.value
    .map(item => item.mapper1_id_value)
    .filter(Boolean);

  // Extract keys to translate from detail
  const keysToTranslateDetail = toolTipsDetail.value
    .map(d => d.description)
    .filter(Boolean);

  const messages = await loadMessages([...keysToTranslateMaster, ...keysToTranslateDetail]);
  translatedToolTips.value = { ...messages };

  const data = [
    Labels.managementId,
    Labels.searchId,
    Labels.pagesId,
    Labels.mailSettingsId,
    Labels.usersPermissionsId,
    Labels.transactionsReportId,
    Labels.usersId,
    Labels.productsId,
    Labels.notificationsId,
    Labels.welcomeId,
    Labels.welcomeTitleId,
    Labels.notificationsTitleId,
    Labels.notificationsIntroId,
    Labels.settingsIntroId,
    Labels.searchIntroId,
    Labels.languageIntroId,
    Labels.currencyIntroId,
    Labels.profileMenuIntroId,
    Labels.profileMenuTitleId,
    Labels.nextId,
    Labels.previousId,
    Labels.finishId,
    Labels.subMainId,
    Labels.subPlatformId,
    Labels.subPlatformTitleId,
    Labels.subMainTitleId,
  ];

  await loadMessages(data);
});

window.addEventListener("message", (event) => awaitEvent(event), false);
async function awaitEvent(event) {
  if (event.data == "menuChange") {
    label.value = localStorage.getItem("breadcrumb");
  }
}


breadcrumb.value = capitalizeFirstLetter(route.path.split("/").pop());
setBreadcrumb(breadcrumb);

router.afterEach((to: any, from: any) => {
  breadcrumb.value = capitalizeFirstLetter(to.path.split("/").pop());
  setBreadcrumb(breadcrumb);
});

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
function setBreadcrumb(breadcrumb: any) {
  const isNumeric = Number(breadcrumb.value);
  if (!isNaN(isNumeric)) {
    breadcrumb.value = capitalizeFirstLetter("html-preview");
  }
}

function calculatePlacement() {
  if (window.innerWidth > 600) {
    return "bottom-end";
  } else {
    ("bottom-start");
  }
}

function getBreadcrumbRoute() {
  return "/management";
}

const hideSearchDropdown = () => {
  searchDropdown.value = false;
};

function goToInbox() {
  router.push("/management/inbox");
}
function selectTheme(theme: string) {
  store.dispatch("setTheme", theme);

  if (document.getElementById("subapp-viewport-platform") == null) {
    location.reload();
  }

  const targetOrigin = window.location.origin;
  var element = document.getElementById("subapp-viewport");
  var styles = rightDiv.value.getElementsByTagName("style");
  removeStyles(styles);

  window.postMessage("layout change", targetOrigin);
}

function removeStyles(styles: any) {
  var elementstoRemove: any[] = [];
  styles.forEach((element: { [x: string]: any; }) => {
    if (element["Symbol(style-modified-qiankun)"]) {
      //element.remove();
      elementstoRemove.push(element);
    }
  });
  elementstoRemove.forEach((element) => {
    element.remove();
  });
}
function goToDashboard() {
  router.push("/management");
}

function startHints() {
  const stepsArray = toolTipsData.value
    .slice()
    .sort((a, b) => (a.ordinal_position ?? 999) - (b.ordinal_position ?? 999))
    .map(item => {
      const el = document.getElementById(item.mapper2_id);

      // Title from master
      const titleText = translatedToolTips.value[item.mapper1_id_value] || item.mapper1_id_value;

      // Intro from matching detail
      const detailMatch = toolTipsDetail.value.find(d => d.value === item.mapper1_id_value);
      const introText = detailMatch
        ? translatedToolTips.value[detailMatch.description] || detailMatch.description
        : titleText; // fallback to master

      return el
        ? { element: el, title: t(titleText || ''), intro: t(introText || '') }
        : null;
    })
    .filter(Boolean);

  if (!stepsArray.length) return console.warn("No valid steps found");

  introJs()
    .setOptions({
      steps: stepsArray,
      showProgress: true,
      showBullets: false,
      exitOnOverlayClick: false,
      nextLabel: t(Labels.nextId),
      prevLabel: t(Labels.previousId),
      doneLabel: t(Labels.finishId),
    })
    .start();
}


</script>
